/* eslint-disable quotes */
'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail,
  Phone,
  User,
  MessageSquare,
  Check,
  Building,
  MapPin,
  Calendar,
} from 'lucide-react';
import { useLanguage } from '@/app/context/language-context';
// import { Button } from './components/ui/button';
// import { Input } from '.components/ui/input';
// import { Textarea } from './components/ui/textarea';
// import { Label } from './components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
// import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { event as gaEvent } from '@/lib/gtag'; // adjust path if needed
import { submitGeneralFormToPixxiCRM } from '@/app/utils/pixxi-api';

const ContactFormModal = ({
  isOpen,
  onClose,
  title = null,
  subtitle = null,
}) => {
  const { t } = useLanguage();
  const [feedback, setFeedback] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+971',
    propertyType: '',
    location: '',
    budget: '',
    timeframe: '',
    message: '',
  });
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(''); // ✅ Error state

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(''); // clear error when typing
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const nextStep = () => {
    // Validate required fields in Step 1
    if (step === 1) {
      const { name, email, phone } = formData;
      if (!name || !email || !phone) {
        alert('Please fill in all required fields');
        return;
      }
    }

    setError('');
    setStep((prev) => prev + 1);
  };

  const prevStep = () => {
    setError('');
    setStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // ✅ Prevent auto-close when validation fails

    const { name, email, phone, propertyType, budget } = formData;

    // Step 1 validation
    if (step === 1) {
      if (!name || !email || !phone) {
        setError('Please fill in all required fields before continuing.');
        return;
      }
      setStep(2);
      return;
    }

    if (step === 2) {
      if (!propertyType || !budget) {
        // alert('Please fill in all required fields');
        setError(
          'Please select both property type and budget before submitting.'
        );
        return; // ✅ Stop form from continuing
      }
    }

    // setError('');
    // setIsSubmitting(true);

    const submitData = {
      name: name,
      phone: phone,
      email: email,

      extraData: {
        title: title
          ? title
          : 'Website Lead - Get personalized recommendations',
        property_type: formData?.propertyType || '',
        location: formData?.location || '',
        budget: formData?.budget || '',
        source: 'Website Lead form for property search',
        timeframe: formData?.timeframe || '',
        message: formData?.message || '',
      },
    };

    try {
      // Simulate a delay for demonstration purposes
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const result = await submitGeneralFormToPixxiCRM(submitData);

      if (result.success) {
        setFeedback('Your message has been submitted successfully!');

        gaEvent({
          action: 'submit_contact_form',
          category: 'Contact',
          label: `Lead: ${formData.name} | Type: ${formData.propertyType || 'N/A'}`,
        });

        // Simulate API call
        setTimeout(() => {
          setIsSubmitting(false);
          setIsSuccess(true);

          // Reset after showing success message
          setTimeout(() => {
            setIsSuccess(false);
            setStep(1);
            setFormData({
              name: '',
              email: '',
              phone: '',
              countryCode: '+971',
              propertyType: '',
              location: '',
              budget: '',
              timeframe: '',
              message: '',
            });
            onClose();
          }, 5000);
        }, 1500);
      } else {
        setSubmitStatus('error');
        setSubmitMessage(
          result.message || 'Failed to submit form. Please try again.'
        );
      }
    } catch (error) {
      setFeedback(
        'Error: There was a problem with your submission. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepIndicator = () => {
    return (
      <div className='flex justify-center mb-6'>
        {[1, 2].map((num) => (
          <div key={num} className='flex items-center'>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                num === step
                  ? 'bg-primarycolor text-white'
                  : num < step
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}
            >
              {num < step ? <Check className='w-4 h-4' /> : num}
            </div>
            {num < 2 && (
              <div
                className={`w-10 h-1 ${
                  step > num ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                }`}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-[550px] p-0 overflow-hidden bg-white dark:bg-gray-900'>
        <div className='bg-gradient-to-r from-primarycolor/60 to-secondarycolor/60 p-6 text-black font-bold dark:text-secondarycolor '>
          <DialogTitle className='text-2xl font-bold mb-2'>
            {isSuccess ? 'Thank You!' : 'Find Your Dream Property'}
          </DialogTitle>
          <DialogDescription className='text-gray-800 max-w-lg font-bold dark:text-black text-lg'>
            {isSuccess
              ? 'Our expert agents will get back to you shortly.'
              : "Tell us what you're looking for and our expert agents will help you find the perfect property."}
          </DialogDescription>
        </div>

        <AnimatePresence mode='wait'>
          {isSuccess ? (
            <motion.div
              key='success'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className='p-6 flex flex-col items-center justify-center'
            >
              <div className='w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-4'>
                <Check className='w-8 h-8 text-green-600 dark:text-green-400' />
              </div>
              <h3 className='text-xl font-semibold text-gray-900 dark:text-white mb-2'>
                Request Submitted!
              </h3>
              <p className='text-gray-600 dark:text-gray-400 text-center mb-6'>
                We've received your inquiry and will contact you within 24
                hours.
              </p>
              <motion.button
                onClick={onClose}
                className='px-2 sm:px-4 py-1 sm:py-2 bg-primarycolor hover:bg-secondarycolor hover:text-black'
              >
                Close
              </motion.button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className='p-6'>
              {renderStepIndicator()}

              {error && <p className='text-red-500 text-sm mb-4'>{error}</p>}

              {step === 1 && (
                <motion.div
                  key='step1'
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className='space-y-4'
                >
                  <div className='space-y-2'>
                    <Label htmlFor='name'>Full Name *</Label>
                    <div className='flex items-center border rounded-md focus-within:ring-1 focus-within:ring-primarycolor'>
                      <User className='ml-3 h-4 w-4 text-gray-400' />
                      <Input
                        id='name'
                        name='name'
                        value={formData.name}
                        onChange={handleChange}
                        placeholder='John Doe'
                        required
                        autoFocus
                        maxLength={25} // <-- Set your desired limit here
                        className='text-[16px] border-0 focus-visible:ring-0 focus-visible:ring-offset-0'
                      />
                    </div>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='email'>Email Address *</Label>
                    <div className='flex items-center border rounded-md focus-within:ring-1 focus-within:ring-primarycolor'>
                      <Mail className='ml-3 h-4 w-4 text-gray-400' />
                      <Input
                        id='email'
                        name='email'
                        type='email'
                        value={formData.email}
                        onChange={handleChange}
                        placeholder='john.doe@example.com'
                        required
                        maxLength={25} // <-- Set your desired limit here
                        className='text-[16px] border-0 focus-visible:ring-0 focus-visible:ring-offset-0'
                      />
                    </div>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='phone'>Phone Number *</Label>
                    <div className='flex items-center'>
                      <Select
                        value={formData.countryCode}
                        onValueChange={(value) =>
                          handleSelectChange('countryCode', value)
                        }
                      >
                        <SelectTrigger className='w-[100px] border-r-0 rounded-r-none'>
                          <SelectValue placeholder='+971' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='+971'>UAE +971</SelectItem>
                          <SelectItem value='+1'>US +1</SelectItem>
                          <SelectItem value='+44'>UK +44</SelectItem>
                          <SelectItem value='+91'>IN +91</SelectItem>
                          <SelectItem value='+86'>CN +86</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className='flex-1 flex items-center border rounded-md rounded-l-none focus-within:ring-1 focus-within:ring-primarycolor'>
                        <Phone className='ml-3 h-4 w-4 text-gray-400' />
                        <Input
                          id='phone'
                          name='phone'
                          type='tel'
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder='50 123 4567'
                          required
                          maxLength={10} // <-- Set your desired limit here
                          className='text-[16px] border-0 focus-visible:ring-0 focus-visible:ring-offset-0'
                        />
                      </div>
                    </div>
                  </div>

                  <DialogFooter className='flex flex-col sm:flex-row sm:justify-between gap-2 pt-4'>
                    <motion.button
                      type='button'
                      variant='outline'
                      className='px-2 sm:px-4 py-1 sm:py-2 hover:bg-gray-200'
                      onClick={onClose}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      type='button'
                      onClick={nextStep}
                      className='px-2 sm:px-4 py-1 sm:py-2 bg-primarycolor hover:bg-secondarycolor hover:text-black'
                    >
                      Next
                    </motion.button>
                  </DialogFooter>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key='step2'
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className='space-y-4'
                >
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='propertyType'>Property Type *</Label>
                      <div className='flex items-center border rounded-md'>
                        <Building className='ml-3 h-4 w-4 text-gray-400' />
                        <Select
                          value={formData.propertyType}
                          onValueChange={(value) =>
                            handleSelectChange('propertyType', value)
                          }
                        >
                          <SelectTrigger className='border-0 focus:ring-0'>
                            <SelectValue placeholder='Select type' />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='apartment'>Apartment</SelectItem>
                            <SelectItem value='villa'>Villa</SelectItem>
                            <SelectItem value='penthouse'>Penthouse</SelectItem>
                            <SelectItem value='townhouse'>Townhouse</SelectItem>
                            <SelectItem value='commercial'>
                              Commercial
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='location'>Preferred Location</Label>
                      <div className='flex items-center border rounded-md focus-within:ring-1 focus-within:ring-primarycolor'>
                        <MapPin className='ml-3 h-4 w-4 text-gray-400' />
                        <Input
                          id='location'
                          name='location'
                          value={formData.location}
                          onChange={handleChange}
                          placeholder='Downtown Dubai'
                          maxLength={25} // <-- Set your desired limit here
                          className='text-[16px] border-0 focus-visible:ring-0 focus-visible:ring-offset-0'
                        />
                      </div>
                    </div>
                  </div>

                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='budget'>Budget Range (AED) *</Label>
                      <div className='flex items-center border rounded-md focus-within:ring-1 focus-within:ring-primarycolor'>
                        <Select
                          value={formData.budget}
                          onValueChange={(value) =>
                            handleSelectChange('budget', value)
                          }
                        >
                          <SelectTrigger className='border-0 focus:ring-0'>
                            <SelectValue placeholder='Select budget' />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='<1M'>Below 1M</SelectItem>
                            <SelectItem value='1M-3M'>1M - 3M</SelectItem>
                            <SelectItem value='3M-5M'>3M - 5M</SelectItem>
                            <SelectItem value='5M-10M'>5M - 10M</SelectItem>
                            <SelectItem value='>10M'>Above 10M</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='timeframe'>Estimated Purchase Date</Label>
                      <div className='flex items-center border rounded-md focus:ring-0'>
                        <Calendar className='ml-3 h-4 w-4 text-gray-400' />
                        <Select
                          value={formData.timeframe}
                          onValueChange={(value) =>
                            handleSelectChange('timeframe', value)
                          }
                        >
                          <SelectTrigger className='border-0 focus:ring-0'>
                            <SelectValue placeholder='Select Purchase Date' />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='immediate'>Immediate</SelectItem>
                            <SelectItem value='1-3 months'>
                              1-3 months
                            </SelectItem>
                            <SelectItem value='3-6 months'>
                              3-6 months
                            </SelectItem>
                            <SelectItem value='6+ months'>6+ months</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='message'>Additional Requirements</Label>
                    <div className='flex items-start border rounded-md focus-within:ring-1 focus-within:ring-primarycolor'>
                      <MessageSquare className='ml-3 mt-3 h-4 w-4 text-gray-400 flex-shrink-0' />
                      <Textarea
                        id='message'
                        name='message'
                        value={formData.message}
                        onChange={handleChange}
                        placeholder='Tell us about your specific requirements...'
                        rows={4}
                        maxLength={500} // <-- Set your desired limit here
                        className='text-[16px] border-0 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none'
                      />
                    </div>
                  </div>

                  <DialogFooter className='flex flex-col sm:flex-row sm:justify-between gap-2 pt-4'>
                    <motion.button
                      type='button'
                      variant='outline'
                      onClick={prevStep}
                      className='px-2 sm:px-4 py-1 sm:py-2 hover:bg-gray-200'
                    >
                      Back
                    </motion.button>
                    <motion.button
                      type='subbmit'
                      disabled={isSubmitting}
                      className='flex items-center px-2 sm:px-4 py-1 sm:py-2 bg-primarycolor hover:bg-secondarycolor hover:text-black'
                    >
                      {isSubmitting ? (
                        <span className='flex items-center gap-2'>
                          <svg
                            className='animate-spin h-4 w-4 text-white'
                            xmlns='http://www.w3.org/2000/svg'
                            fill='none'
                            viewBox='0 0 24 24'
                          >
                            <circle
                              className='opacity-25'
                              cx='12'
                              cy='12'
                              r='10'
                              stroke='currentColor'
                              strokeWidth='4'
                            ></circle>
                            <path
                              className='opacity-75'
                              fill='currentColor'
                              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                            ></path>
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        'Submit Inquiry'
                      )}
                    </motion.button>
                  </DialogFooter>
                </motion.div>
              )}
            </form>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormModal;
